import React, { FC, PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';
import { TimeMs } from '../timeline/types';
import { useModelComposition } from '../model/use-model-composition';
import { useEditorCanvas } from '../canvas/hooks/use-editor-canvas';
import { useMedia } from '../media';
import { PlaybackContext, PlaybackContextValue } from './playback-context';
import { playheadStore } from './playhead-store';
import { usePlaybackEngine } from './playback-engine';

export const PlaybackProvider: FC<PropsWithChildren> = ({ children }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(1);
  const [muted, setMuted] = useState(false);

  const { resumeAudio } = useMedia();
  const { canvas } = useEditorCanvas();
  const composition = useModelComposition();

  const pause = useCallback(() => setIsPlaying(false), []);

  // The playhead runs on animation frames, which a hidden tab stops: the videos
  // would keep their own audio going while nothing advances the scene.
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.hidden) setIsPlaying(false);
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, []);

  const play = useCallback(async () => {
    await resumeAudio();
    setIsPlaying(true);
  }, [resumeAudio]);

  const toggle = useCallback(async () => {
    if (isPlaying) {
      pause();
      return;
    }
    await play();
  }, [isPlaying, play, pause]);

  const seek = useCallback((t: TimeMs) => playheadStore.set(Math.max(0, t)), []);

  usePlaybackEngine({ canvas, composition, isPlaying, volume, muted, onEnd: pause });

  const value = useMemo<PlaybackContextValue>(
    () => ({ isPlaying, play, pause, toggle, seek, volume, setVolume, muted, setMuted }),
    [isPlaying, play, pause, toggle, seek, volume, muted]
  );

  return <PlaybackContext.Provider value={value}>{children}</PlaybackContext.Provider>;
};
